<template>
  <div
    aria-modal="true"
    class="slideshow-share-phone"
    role="dialog"
    tabindex="-1"
    ref="root"
    :aria-labelledby="titleId"
  >
    <!-- Phones share the film itself, to stories and chats, through the phone's own share sheet. -->
    <div class="slideshow-share-phone__head">
      <h2 class="slideshow-share-phone__title" :id="titleId">{{ title }}</h2>
      <button
        class="slideshow-share-phone__close"
        type="button"
        :aria-label="texts.close"
        @click="emit('close')"
      >
        <SlideshowIcon name="close" />
      </button>
    </div>

    <div class="slideshow-share-phone__preview" ref="previewWrap">
      <div class="slideshow-share-phone__frame" :style="frameStyle">
        <canvas aria-hidden="true" ref="previewCanvas" />
      </div>
      <p class="slideshow-share-phone__meta" v-if="exporter.videoMode.value">
        {{ meta }}
      </p>
    </div>

    <div class="slideshow-share-phone__actions">
      <!-- This phone can't make the film: share the link, no error message. -->
      <template v-if="!exporter.videoMode.value">
        <p class="slideshow-share-phone__lead">{{ texts.linkLead }}</p>
        <button
          class="slideshow-share-phone__primary"
          type="button"
          @click="shareTheLink"
        >
          <SlideshowIcon name="share" />
          {{ texts.shareLinkButton }}
        </button>
        <SlideshowCopyButton
          class="slideshow-share-phone__secondary"
          :copied="copied"
          @click="copyLink()"
        />
      </template>

      <template v-else-if="shared">
        <p class="slideshow-share-phone__done">
          {{ sprintf(texts.sharedThanks, name) }}
        </p>
        <p class="slideshow-share-phone__helper">{{ texts.reelTip }}</p>
        <div class="slideshow-share-phone__ctas">
          <button
            class="slideshow-share-phone__primary"
            type="button"
            @click="share"
          >
            {{ texts.shareAgain }}
          </button>
          <button
            class="slideshow-share-phone__secondary"
            type="button"
            @click="emit('close')"
          >
            {{ texts.close }}
          </button>
        </div>
      </template>

      <!-- One button; the phone's own share sheet does the rest. -->
      <template v-else>
        <p class="slideshow-share-phone__done" v-if="waitingForTap">
          <SlideshowIcon name="check" />
          {{ texts.readyFilm }}
        </p>
        <div
          aria-live="polite"
          class="slideshow-share-phone__progress"
          v-if="waiting && exporter.making.value"
        >
          <div class="slideshow-share-phone__progress-row">
            <b>{{ texts.preparingFilm }}</b>
            <span>{{ exporter.percent.value }} %</span>
          </div>
          <div class="slideshow-share-phone__bar">
            <i :style="{ width: `${exporter.percent.value}%` }" />
          </div>
          <p class="slideshow-share-phone__helper">
            {{ texts.opensWhenReady }}
          </p>
          <button
            class="slideshow-share-phone__textlink"
            type="button"
            @click="cancel"
          >
            {{ texts.cancel }}
          </button>
        </div>
        <button
          class="slideshow-share-phone__primary"
          type="button"
          v-else
          @click="share"
        >
          <SlideshowIcon name="share" />
          {{ waitingForTap ? texts.shareReady : texts.shareFilm }}
        </button>
        <p
          aria-live="polite"
          class="slideshow-share-phone__notice"
          v-if="exporter.notice.value"
        >
          {{ exporter.notice.value }}
        </p>
        <button
          class="slideshow-share-phone__primary"
          type="button"
          v-if="exporter.interrupted.value === 'failed'"
          @click="exporter.retry"
        >
          {{ texts.retry }}
        </button>
        <p class="slideshow-share-phone__helper" v-if="downloadedNote">
          {{ texts.savedDownloads }}
        </p>
      </template>

      <p aria-live="polite" class="slideshow-share-phone__notice" v-if="note">
        {{ note }}
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    useId,
    watch,
  } from "vue";
  import {
    FILM_SIZE,
    type FilmExport,
    useFilmExport,
  } from "../../composables/useFilmExport";
  import { useShareLink } from "../../composables/useShareLink";
  import {
    assets,
    forFilm,
    slideshowTexts,
  } from "../../composables/useSlideshow";
  import { SlideshowPlayer } from "../../slideshow/player";
  import type { PageContent } from "../../slideshow/playlist";
  import { sprintf } from "../../slideshow/playlist";
  import { download, shareFile, shareLink } from "../../slideshow/share";
  import { track } from "../../slideshow/track";
  import SlideshowCopyButton from "./SlideshowCopyButton.vue";
  import SlideshowIcon from "./SlideshowIcon.vue";

  const props = defineProps<{
    content: PageContent;
    /** The viewer's film, often made already while the slideshow played. */
    exporter?: FilmExport;
    /** The memorial page, opening on the slideshow (…#bildspel). */
    pageUrl: string;
    name: string;
  }>();
  const emit = defineEmits<{ close: [] }>();

  /** Room under the preview for its length line, px. */
  const META_SPACE = 32;
  // Every phone destination (stories, chats) shows a standing film well.
  const FORMAT = "portrait";

  const texts = slideshowTexts();
  const titleId = `slideshow-share-${useId()}`;
  const { copied, copyLink, filmText, linkText, note, urlFor } = useShareLink(
    () => props.pageUrl,
    () => props.content.deceased.name,
    "share_view"
  );
  const exporter =
    props.exporter ??
    useFilmExport(
      () => props.content,
      () => props.name,
      true
    );

  const shared = ref(false);
  /** Someone tapped and is waiting for the film. */
  const waiting = ref(false);
  const waitingForTap = ref(false);
  const downloadedNote = ref(false);
  const root = ref<HTMLElement | null>(null);

  const title = computed(() =>
    exporter.videoMode.value
      ? forFilm("shareFilmTitle", "lifeShareTitle")
      : forFilm("linkTitle", "lifeShareTitle")
  );
  const meta = computed(() => {
    const seconds = Math.round(exporter.film(FORMAT).duration);
    const length = sprintf(
      texts.filmDuration,
      Math.floor(seconds / 60),
      seconds % 60
    );
    return sprintf(texts.filmMetaMusic, length);
  });

  // ----- the preview: exactly the film that will be shared -----

  const previewWrap = ref<HTMLElement | null>(null);
  const previewCanvas = ref<HTMLCanvasElement | null>(null);
  const wrapSize = ref({ h: 0, w: 0 });
  let preview: SlideshowPlayer | null = null;
  let resize: ResizeObserver | null = null;

  // As large as the space allows, in the film's shape, without stretching it.
  const frameStyle = computed(() => {
    const [aw, ah] = FILM_SIZE[FORMAT];
    const W = wrapSize.value.w;
    const H = wrapSize.value.h - (exporter.videoMode.value ? META_SPACE : 0);
    if (!(W && H > 0)) {
      return { aspectRatio: `${aw} / ${ah}` };
    }
    const scale = Math.min(W / aw, H / ah);
    return {
      height: `${Math.floor(ah * scale)}px`,
      width: `${Math.floor(aw * scale)}px`,
    };
  });

  function startPreview() {
    if (!previewCanvas.value) {
      return;
    }
    if (preview) {
      preview.setTimeline(exporter.film(FORMAT), false);
    } else {
      preview = new SlideshowPlayer(
        previewCanvas.value,
        exporter.film(FORMAT),
        {
          assets,
          captions: true,
          loop: true,
          reducedMotion: exporter.reducedMotion.value,
          shareSafe: true,
        }
      );
    }
    preview.play();
  }

  // Faces and photo sizes arrived: the preview follows, as the film will.
  watch(exporter.fullContent, () =>
    preview?.setTimeline(exporter.film(FORMAT))
  );

  // The preview plays while the film is made in the background; once someone is waiting,
  // the phone's full power goes to the film.
  watch([waiting, exporter.making], ([isWaiting, isMaking]) => {
    if (isWaiting && isMaking) {
      preview?.pause();
    } else {
      preview?.play();
    }
  });

  watch(exporter.reducedMotion, () => {
    exporter.reset();
    preview?.setReducedMotion(exporter.reducedMotion.value);
    startPreview();
  });

  // ----- sharing -----

  async function handOver(file: File) {
    waitingForTap.value = false;
    const outcome = await shareFile(file, filmText.value);
    if (outcome === "shared") {
      shared.value = true;
      track({
        action: "video_shared",
        format: FORMAT,
        platform: "os",
        surface: "share_view",
      });
    } else if (outcome === "needs-tap") {
      waitingForTap.value = true;
    } else if (outcome === "unsupported") {
      download(file);
      downloadedNote.value = true;
      track({
        action: "video_downloaded",
        format: FORMAT,
        platform: "os-fallback",
        surface: "share_view",
      });
    }
  }

  async function share() {
    shared.value = false;
    const ready = exporter.readyFile(FORMAT);
    if (ready) {
      await handOver(ready);
      return;
    }
    waiting.value = true;
    const file = await exporter.ensureFile(FORMAT);
    waiting.value = false;
    if (!file) {
      return;
    }
    // Straight on to the share sheet. Browsers open it only shortly after a tap; when
    // making the film took longer, they refuse, and the button asks for the one tap more.
    await handOver(file);
  }

  function cancel() {
    exporter.cancel();
    waiting.value = false;
  }

  async function shareTheLink() {
    const outcome = await shareLink(urlFor("link"), linkText.value);
    if (outcome === "shared") {
      track({ action: "link_shared", platform: "os", surface: "share_view" });
    } else if (outcome === "unsupported") {
      await copyLink();
    }
  }

  onMounted(() => {
    if (previewWrap.value) {
      resize = new ResizeObserver(([entry]) => {
        wrapSize.value = {
          h: entry.contentRect.height,
          w: entry.contentRect.width,
        };
      });
      resize.observe(previewWrap.value);
    }
    startPreview();
    track({ action: "share_open", format: FORMAT });
    // Into the view itself, not onto a button: no focus ring on open, Tab reaches the buttons.
    nextTick(() => root.value?.focus({ preventScroll: true }));
    // Start on the film right away, so it's usually ready by the time they tap.
    const saveData = (
      navigator as Navigator & { connection?: { saveData?: boolean } }
    ).connection?.saveData;
    if (exporter.videoMode.value && !saveData) {
      exporter.ensureFile(FORMAT);
    }
  });

  onBeforeUnmount(() => {
    resize?.disconnect();
    preview?.destroy();
    preview = null;
  });
</script>
